/**
 * eventCache — a tiny shared-singleton cache + in-flight deduper for
 * per-event resources (photos, public-stats, spreading, etc.).
 *
 * Framework-agnostic so it can be imported by hooks, components, and
 * imperative callers (toasts, share sheets) without React wiring.
 *
 * Why a custom cache instead of swr / react-query?
 * ------------------------------------------------
 * EventDrop's data is timeline-coupled to a single live event — the
 * cache lifecycle maps neatly to the event session. Pulling in a full
 * SWR/RQ runtime would add bundle weight AND fight with the existing
 * useEventRecovery/EventSessionProvider primitives. ~40 lines does the
 * job: TTL + inflight dedupe + invalidation.
 *
 * Cache key shape:  `{event_id}:{resource}`  e.g.
 *   "event_05d1cd09df87:photos"
 *   "event_05d1cd09df87:public-stats"
 *   "event_05d1cd09df87:spreading"
 *
 * TTL: 60s by default. Resources can override via `fetchOnce(..., {ttl})`.
 */

const DEFAULT_TTL_MS = 60_000;
const cache = new Map();      // key → { data, t, ttl }
const inflight = new Map();   // key → Promise

export const getCached = (key) => {
  const v = cache.get(key);
  if (!v) return null;
  if (Date.now() - v.t > v.ttl) {
    cache.delete(key);
    return null;
  }
  return v.data;
};

export const setCached = (key, data, ttl = DEFAULT_TTL_MS) => {
  cache.set(key, { data, t: Date.now(), ttl });
};

/**
 * fetchOnce — returns the cached value if fresh, else dedupes against
 * any in-flight request for the same key. Network errors are NOT
 * cached (so a transient failure doesn't poison the next 60s).
 */
export const fetchOnce = async (key, fetcher, { ttl = DEFAULT_TTL_MS } = {}) => {
  const cached = getCached(key);
  if (cached !== null) return cached;
  if (inflight.has(key)) return inflight.get(key);
  const p = (async () => {
    try {
      const data = await fetcher();
      setCached(key, data, ttl);
      return data;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
};

export const invalidate = (keyOrPrefix) => {
  // Substring match — `invalidate("event_xxx")` clears every per-event
  // resource at once (handy on logout / event-switch).
  for (const k of Array.from(cache.keys())) {
    if (k === keyOrPrefix || k.startsWith(`${keyOrPrefix}:`)) cache.delete(k);
  }
};

export const clearAll = () => {
  cache.clear();
  inflight.clear();
};
